import { Component, ElementRef, EventEmitter, forwardRef, Input, Output } from "@angular/core";
import { ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { ModalController } from "ionic-angular";
import { Subject } from "rxjs/Subject";
import { AclCategoryModalComponent } from "./acl-category-modal.component";
import { Membership } from "../../../business-objects/membership/membership.model";
import { ControlComponent } from "../../control.component";
import { AclCategoryControl } from "../acl-category-control.model";
import { isInArray } from "../../../../core/util/is-in-array";
import { VasAppFacade } from "../../../services/vas-app-facade.service";
import { AclCategoryModalModel } from "./acl-category-modal.model";

@Component({
    selector: "acl-category",
    template: `
        <button ion-button type="button" class="show-acl-manager" (click)="onShowAclCategoryModal()">Pick</button>
    `,
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AclCategoryFieldComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => AclCategoryFieldComponent), multi: true}
    ],
    entryComponents: [AclCategoryModalComponent]
})
export class AclCategoryFieldComponent extends ControlComponent implements ControlValueAccessor/*, OnChanges*/ {
    @Input("value") _value = undefined;
    @Input() definition: AclCategoryControl;
    @Input() formGroup: FormGroup;
    @Input() assetId: string;

    @Output() changes: EventEmitter<any> = new EventEmitter();

    protected _displayValue: string = "";
    protected ngUnsubscribe: Subject<void> = new Subject<void>();
    memberships: Array<Membership>;

    constructor(protected elemRef: ElementRef, protected modalCtl: ModalController, protected appFacade: VasAppFacade) {
        super(elemRef);
    }

    ngOnInit(): void {

        if (!this.definition) {
            console.error(`invalid control definition. definition is missing`, this);
            throw new Error(`invalid control definition. definition property is missing`);
        }

        super.ngOnInit();

        this.appFacade.objects.membership.store
            .getAll()
            .take(1)
            .subscribe((memberships: Array<Membership>) => {
                this.memberships = memberships;
            });
    }

    onDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    get displayValue(): any {
        return this._displayValue;
    }

    /**
     * Execute validation
     *
     * The control is only valid if at least one membership contains a reference to the category object
     * - On first load (no changes) we check the app state. In the case that this is a new category we expect there not to be
     * any references, so we must prevent the user from saving the field without tagging at least one membership.
     * - After user has made changes, we check that if the changes were be saved at least one membership would be tagged.
     *
     * @param c
     * @returns {any}
     */
    validate(c: FormControl): any {

        const membershipsById = {};

        if (!this.assetId) {
            throw Error("invalid assetId");
        }

        this.memberships.forEach(membership => {
            membershipsById[membership.id] = membership;
        });

        // if this._value is set, the user has made some changes. We merge these with the app state so we can check
        // that at least membership would be tagged if the changes were saved.
        if (this._value) {
            Object.keys(this._value).map(key => this._value[key]).forEach((membership: Membership) => {
                membershipsById[membership.id] = membership;
            });
        }

        const count = Object
            .keys(membershipsById)
            .map(id => membershipsById[id])
            .filter(membership => isInArray(this.assetId, membership[this.definition.config.category])).length;

        if (!count) {
            return {
                "aclNoMembershipTagged": count
            };
        }

        return undefined;
    }

    onShowAclCategoryModal(): void {

        const modal = this.modalCtl.create(AclCategoryModalComponent, {
            categoryId: this.assetId,
            categoryType: this.definition.config.category,
            memberships: this._value
        });

        modal.onDidDismiss((model: AclCategoryModalModel) => {

            if (model) {
                this.updateValue(model.submitData);
            }
        });

        modal.present();
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/acl-category/form/acl-category.component.ts